import type { MaskitoOptions } from '@maskito/core';
import { maskitoNumberOptionsGenerator, type MaskitoNumberParams } from '@maskito/kit';

/** Options for CADS USD currency Maskito fields. */
export type CadsCurrencyMaskitoOptions = Partial<MaskitoNumberParams> & {
  /** When false, `min` defaults to `0`. Default **false**. */
  readonly allowNegative?: boolean;
};

/**
 * Clamps a numeric amount to inclusive {@link min} and {@link max}.
 */
export function cadsClampCurrencyAmount(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * Builds {@link MaskitoNumberParams} for {@link maskitoParseNumber} / {@link maskitoStringifyNumber}.
 */
export function cadsCurrencyNumberParams(overrides: CadsCurrencyMaskitoOptions = {}): MaskitoNumberParams {
  const allowNegative = overrides.allowNegative ?? false;
  const precision = overrides.precision ?? overrides.maximumFractionDigits ?? 2;
  return {
    prefix: '$',
    thousandSeparator: ',',
    decimalSeparator: '.',
    precision,
    maximumFractionDigits: overrides.maximumFractionDigits ?? precision,
    minimumFractionDigits: overrides.minimumFractionDigits ?? 0,
    min: allowNegative ? (overrides.min ?? Number.MIN_SAFE_INTEGER) : (overrides.min ?? 0),
    max: overrides.max ?? Number.MAX_SAFE_INTEGER,
    minusSign: overrides.minusSign,
    minusPseudoSigns: overrides.minusPseudoSigns,
    negativePattern: overrides.negativePattern,
    postfix: overrides.postfix,
    decimalPseudoSeparators: overrides.decimalPseudoSeparators,
  };
}

/** Full Maskito options for a CADS USD-style currency field. */
export function cadsCurrencyMaskitoOptions(overrides: CadsCurrencyMaskitoOptions = {}): MaskitoOptions {
  return maskitoNumberOptionsGenerator(cadsCurrencyNumberParams(overrides));
}

/** Masked display when the model value is empty (`null`) but the prefix should remain visible. */
export function cadsCurrencyEmptyDisplay(params: MaskitoNumberParams = cadsCurrencyNumberParams()): string {
  return params.prefix ?? '$';
}

/** True when the masked input has no numeric amount (empty or prefix only). */
export function cadsCurrencyDisplayIsEmpty(
  displayValue: string,
  params: MaskitoNumberParams = cadsCurrencyNumberParams()
): boolean {
  const trimmed = displayValue.trim();
  if (trimmed === '') {
    return true;
  }
  const prefix = params.prefix ?? '';
  return prefix.length > 0 && trimmed === prefix;
}
